import { Link, useLocation, useNavigate } from 'react-router-dom';
import { COMPANY_EMAIL, COMPANY_NAME } from '../config/siteInfo';
import { scrollToSectionId } from '../lib/scrollNav';

const navLinks = [
  { label: 'Home', id: 'home' },
  { label: 'Services', id: 'services' },
  { label: 'About', id: 'about' },
  { label: 'Projects', id: 'projects' },
  { label: 'Process', id: 'process' },
  { label: 'Why us', id: 'whyus' },
  { label: 'Contact', id: 'contact' },
];

const serviceLinks = [
  'Steel fabrication',
  'Steel beams & columns',
  'Design & connections',
  'Staircases & mezzanines',
  'Gates & balustrades',
  'Welding & site surveys',
];

const CREDIT_URL = 'https://hujaifamiyanji.co.uk/';

function sectionTo(linkId) {
  return linkId === 'home' ? '/' : `/#${linkId}`;
}

export default function Footer({ siteSettings, footer }) {
  const year = new Date().getFullYear();
  const siteName = siteSettings?.siteName || COMPANY_NAME;
  const email = siteSettings?.contactEmail || COMPANY_EMAIL;
  const phone = siteSettings?.phone || '+44 (0) 1234 567 890';
  const location = siteSettings?.location || 'Midlands, United Kingdom';
  const loc = useLocation();
  const navigate = useNavigate();

  const brandDescription =
    footer?.brandDescription ||
    'Premium structural and architectural steel fabrication — engineered, fabricated, and delivered with precision.';

  const footerServiceLinks = footer?.serviceLinks?.length ? footer.serviceLinks : serviceLinks;
  const socialLinks = footer?.socialLinks?.length
    ? footer.socialLinks
    : [
        { label: 'LinkedIn', url: 'https://www.linkedin.com' },
        { label: 'Facebook', url: 'https://www.facebook.com' },
        { label: 'Instagram', url: 'https://www.instagram.com' },
      ];

  const handleLogo = (e) => {
    e.preventDefault();
    if (loc.pathname === '/') scrollToSectionId('home');
    else navigate('/');
  };

  const handleSection = (e, id) => {
    e.preventDefault();
    if (loc.pathname === '/') scrollToSectionId(id);
    else navigate(sectionTo(id));
  };

  const socialIcon = (label) => {
    const key = String(label || '').toLowerCase();
    if (key.includes('facebook')) {
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
      );
    }
    if (key.includes('instagram')) {
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" aria-hidden>
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    );
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-main">
          <div>
            <Link
              to="/"
              className="navbar-logo"
              style={{ marginBottom: 0 }}
              onClick={handleLogo}
              aria-label={`${siteName} — Home`}
            >
              <div className="logo-icon" aria-hidden>
                <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden>
                  <path
                    d="M10 2L17 6V10C17 14 13.5 17.5 10 18C6.5 17.5 3 14 3 10V6L10 2Z"
                    fill="white"
                  />
                </svg>
              </div>
              <span className="logo-text-main">{siteName}</span>
            </Link>
            <p className="footer-brand-desc">{brandDescription}</p>
            <div className="footer-social">
              {socialLinks.map((s) => (
                <a key={s.url} className="soc-btn" href={s.url} aria-label={s.label}>
                  {socialIcon(s.label)}
                </a>
              ))}
            </div>
          </div>

          <div className="footer-col">
            <h5>Quick links</h5>
            <ul>
              {navLinks.map((link) => (
                <li key={link.id}>
                  <Link to={sectionTo(link.id)} onClick={(e) => handleSection(e, link.id)}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <h5>Services</h5>
            <ul>
              {footerServiceLinks.map((s) => (
                <li key={s}>
                  <Link to="/#services" onClick={(e) => handleSection(e, 'services')}>
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <h5>Contact</h5>
            <ul>
              <li>
                <a href={`tel:${phone.replace(/\s|\(|\)/g, '')}`}>{phone}</a>
              </li>
              <li>
                <a href={`mailto:${email}`}>{email}</a>
              </li>
              <li>{location}</li>
              <li style={{ marginTop: 12 }}>
                <Link
                  to="/#contact"
                  onClick={(e) => handleSection(e, 'contact')}
                  style={{ color: 'var(--gold-dark)', fontWeight: 600 }}
                >
                  Get a quote →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-row">
            <p className="footer-copy">
              © {year} <span>{siteName}</span>. All rights reserved.
            </p>
            <div className="footer-legal">
              <Link to="/privacy-policy">Privacy policy</Link>
              <Link to="/terms-and-conditions">Terms & conditions</Link>
              <Link to="/sitemap">Sitemap</Link>
            </div>
          </div>
          <p className="footer-credit">
            Built and designed by{' '}
            <a href={CREDIT_URL} target="_blank" rel="noopener noreferrer">
              Miyanji Tech Solution
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
